//>>> Routing utilities for role-based navigation
import { ENV } from '@configs/env';

const PORTAL_STORAGE_KEY = 'school-crm-portal';

export const SIGN_IN_PATHS = {
    owner: '/owner/sign-in',
    tenant: '/tenant/sign-in',
};

/**
 * Determines if a user role is an owner/admin role
 * Owner roles typically include: super-admin, admin, owner, sub-admin
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const isOwnerRole = (role) => {
    if (!role) return false;

    // NOTE: tenant roles sometimes use "superadmin" for school admins.
    // Owner console roles are kept explicit to avoid routing tenant admins into owner routes.
    const ownerRoles = ['super-admin', 'admin', 'owner', 'sub-admin'];
    return ownerRoles.some((ownerRole) => role.toLowerCase().includes(ownerRole.toLowerCase()));
};

/**
 * Determines if a user role is a tenant role
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const isTenantRole = (role) => {
    if (!role) return false;
    return !isOwnerRole(role) && !isStudentRole(role);
};

/**
 * Determines if a user role is a student role
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const isStudentRole = (role) => {
    if (!role) return false;
    return role.toLowerCase() === 'student';
};

/**
 * Gets the appropriate dashboard route based on user role
 * @param {string} role - User's role
 * @returns {string} Dashboard route path
 */
export const getDashboardRoute = (role) => {
    if (isOwnerRole(role)) return '/dashboard';
    if (isStudentRole(role)) return '/student/dashboard';
    return '/tenant/dashboard';
};

/**
 * Sign-in path for a portal (owner console vs school portal).
 */
export const getSignInPathForPortal = (portal) =>
    portal === 'owner' ? SIGN_IN_PATHS.owner : SIGN_IN_PATHS.tenant;

/**
 * Where to send unauthenticated users (domain + last portal preference).
 */
export const getDefaultGuestSignInPath = () => {
    if (typeof window !== 'undefined') {
        const last = window.sessionStorage?.getItem(PORTAL_STORAGE_KEY);
        if (last === 'owner' || last === 'tenant') {
            return getSignInPathForPortal(last);
        }
    }
    return ENV.IS_OWNER ? SIGN_IN_PATHS.owner : SIGN_IN_PATHS.tenant;
};

/** Persisted on successful login for RequireAuth redirects. */
export const rememberAuthPortal = (portal) => {
    if (typeof window === 'undefined') return;
    if (portal === 'owner' || portal === 'tenant') {
        window.sessionStorage.setItem(PORTAL_STORAGE_KEY, portal);
    }
};
